import React, { useState } from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import infoIcon from "icons/info-new-rows.svg";
import Checkbox, {
  checkboxStates,
} from "@visiblethread/react-components/dist/components/Checkbox";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import { editUserPermissions } from "slices/systemAdmin/actions/systemAdmin";

const EditUserPermissionsModal = ({
  workspaceId,
  username,
  licensed,
  dictionary,
  readabilitySettings,
  acronymSettings,
  discoverySettings,
  excelOutputs,
  userAdmin,
}) => {
  const dispatch = useDispatch();

  const [currentLicensed, setCurrentLicensed] = useState(licensed);
  const [currentDictionary, setCurrentDictionary] = useState(dictionary);
  const [currentReadabilitySettings, setCurrentReadabilitySettings] =
    useState(readabilitySettings);
  const [currentAcronymSettings, setCurrentAcronymSettings] =
    useState(acronymSettings);
  const [currentDiscoverySettings, setCurrentDiscoverySettings] =
    useState(discoverySettings);
  const [currentExcelOutputs, setCurrentExcelOutputs] = useState(excelOutputs);
  const [currentUserAdmin, setCurrentUserAdmin] = useState(userAdmin);

  const handleSave = () => {
    dispatch(
      editUserPermissions(
        workspaceId,
        username,
        currentLicensed,
        currentDictionary,
        currentReadabilitySettings,
        currentAcronymSettings,
        currentDiscoverySettings,
        currentExcelOutputs,
        currentUserAdmin
      )
    );
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EDIT_PERMISSIONS));
  };

  const modalBodyLicensed = () => (
    <div
      className="edit__permissions__modal__licensed"
      data-testid="modal-licensed"
    >
      <div data-title='Making a user "Licensed User" allows them sign in and work with documents. Only "Licensed Users" use a license'>
        <b>Is Licensed User</b>
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentLicensed ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            if (currentLicensed) {
              setCurrentDictionary(false);
              setCurrentReadabilitySettings(false);
              setCurrentAcronymSettings(false);
              setCurrentDiscoverySettings(false);
              setCurrentExcelOutputs(false);
            }
            setCurrentLicensed(!currentLicensed);
          }}
          disabled={false}
          id="cb-edit-permissions-licensed"
          value="edit-permissions-licensed-checkbox"
        />
      </div>
    </div>
  );
  const modalBodyDictionaries = () => (
    <div
      className="edit__permissions__modal__dictionaries"
      data-testid="modal-dictionaries"
    >
      <div data-title="Enabling this permission allows this user edit the Dictionaries for this workspace">
        Can Edit Dictionaries
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentDictionary ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentDictionary(!currentDictionary);
          }}
          disabled={!currentLicensed}
          id="cb-edit-permissions-dictionaries"
          value="edit-permissions-dictionaries-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyReadabilitySettings = () => (
    <div
      className="edit__permissions__modal__readability__settings"
      data-testid="modal-readability-settings"
    >
      <div data-title="Enabling this permission allows this user edit the Readability Settings for this workspace">
        Can Edit Readability Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentReadabilitySettings
              ? checkboxStates.checked
              : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentReadabilitySettings(!currentReadabilitySettings);
          }}
          disabled={!currentLicensed}
          id="cb-edit-permissions-readability-settings"
          value="edit-permissions-readability-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyAcronymSettings = () => (
    <div
      className="edit__permissions__modal__acronym__settings"
      data-testid="modal-acronym-settings"
    >
      <div data-title="Enabling this permission allows this user edit the Acronym Settings for this workspace">
        Can Edit Acronym Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentAcronymSettings
              ? checkboxStates.checked
              : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentAcronymSettings(!currentAcronymSettings);
          }}
          disabled={!currentLicensed}
          id="cb-edit-permissions-acronym-settings"
          value="edit-permissions-acronym-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyDiscoverySettings = () => (
    <div
      className="edit__permissions__modal__discovery__settings"
      data-testid="modal-discovery-settings"
    >
      <div data-title="Enabling this permission allows this user edit the Discovery Settings for this workspace">
        Can Edit Discovery Settings
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentDiscoverySettings
              ? checkboxStates.checked
              : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentDiscoverySettings(!currentDiscoverySettings);
          }}
          disabled={!currentLicensed}
          id="cb-edit-permissions-discovery-settings"
          value="edit-permissions-discovery-settings-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyExcelOutputs = () => (
    <div
      className="edit__permissions__modal__excel__outputs"
      data-testid="modal-excel-outputs"
    >
      <div data-title="Enabling this permission allows this user edit the Excel Outputs for this workspace">
        Can Edit Excel Outputs
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentExcelOutputs ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentExcelOutputs(!currentExcelOutputs);
          }}
          disabled={!currentLicensed}
          id="cb-edit-permissions-excel-outputs"
          value="edit-permissions-excel-outputs-checkbox"
        />
      </div>
    </div>
  );

  const modalBodyUserAdmin = () => (
    <div
      className="edit__permissions__modal__user__admin"
      data-testid="modal-user-admin"
    >
      <div data-title="This allows the user create modify and delete users within the workspace">
        <b>Can Administer Users</b>
        <img src={infoIcon} alt="info icon" />
      </div>
      <div>
        <Checkbox
          state={
            currentUserAdmin ? checkboxStates.checked : checkboxStates.empty
          }
          handleChange={() => {
            setCurrentUserAdmin(!currentUserAdmin);
          }}
          disabled={false}
          id="cb-edit-permissions-user-admin"
          value="edit-permissions-user-admin-checkbox"
        />
      </div>
    </div>
  );

  return (
    <Modal
      data-testid="editUserPermissionsModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <b className="edit__permissions__modal__header">Edit Permissions</b>
      </Modal.Header>
      <Modal.Body>
        <div className="edit__permissions__modal">
          {modalBodyLicensed()}
          {modalBodyDictionaries()}
          {modalBodyReadabilitySettings()}
          {modalBodyAcronymSettings()}
          {modalBodyDiscoverySettings()}
          {modalBodyExcelOutputs()}
          {modalBodyUserAdmin()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-test="saveButton"
          variant={buttonVariants.primary}
          onClick={handleSave}
          disabled={false}
          content="Save"
        />
        <Button
          data-test="cancelButton"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserPermissionsModal;

EditUserPermissionsModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  licensed: PropTypes.bool.isRequired,
  dictionary: PropTypes.bool.isRequired,
  readabilitySettings: PropTypes.bool.isRequired,
  acronymSettings: PropTypes.bool.isRequired,
  discoverySettings: PropTypes.bool.isRequired,
  excelOutputs: PropTypes.bool.isRequired,
  userAdmin: PropTypes.bool.isRequired,
};
